import React from "react";
import {
  FaBold,
  FaItalic,
  FaStrikethrough,
  FaCode,
  FaHeading,
  FaQuoteRight,
  FaListUl,
  FaListOl,
  FaLink,
  FaImage,
  FaMinus,
} from "react-icons/fa";

export const defaultMarkdownTools = [
  { id: "bold", label: "Bold", icon: FaBold },
  { id: "italic", label: "Italic", icon: FaItalic },
  { id: "strike", label: "Strike", icon: FaStrikethrough },
  { id: "code", label: "Inline Code", icon: FaCode },
  { id: "codeblock", label: "Code Block", icon: FaCode },
  { id: "h2", label: "Heading 2", icon: FaHeading },
  { id: "h3", label: "Heading 3", icon: FaHeading },
  { id: "quote", label: "Quote", icon: FaQuoteRight },
  { id: "ul", label: "Bullet List", icon: FaListUl },
  { id: "ol", label: "Numbered List", icon: FaListOl },
  { id: "link", label: "Link", icon: FaLink },
  { id: "image", label: "Image", icon: FaImage },
  { id: "hr", label: "Divider", icon: FaMinus },
];

const defaultToolMap = defaultMarkdownTools.reduce((acc, tool) => {
  acc[tool.id] = tool;
  return acc;
}, {});

const sizeStyles = {
  xs: {
    wrap: "px-1.5 py-1 gap-1",
    button: "h-5 w-5 text-[10px]",
    icon: "text-xs",
    label: "text-[9px]",
  },
  sm: {
    wrap: "px-2 py-1.5 gap-1.5",
    button: "h-8 w-8 text-xs",
    icon: "text-sm",
    label: "text-[10px]",
  },
  md: {
    wrap: "px-3 py-2 gap-2",
    button: "h-9 w-9 text-xs",
    icon: "text-base",
    label: "text-[11px]",
  },
  lg: {
    wrap: "px-4 py-2.5 gap-2.5",
    button: "h-7 w-7 text-sm",
    icon: "text-lg",
    label: "text-xs",
  },
};

const variantStyles = {
  glass:
    "border border-white/10 bg-black shadow-lg backdrop-blur",
  solid:
    "border border-slate-900/40 bg-black shadow-md",
  ghost:
    "border border-white/5 bg-white/5",
};

function MarkdownToolbar({
  onAction,
  tools = defaultMarkdownTools,
  className = "",
  size = "md",
  variant = "glass",
  layout = "inline",
  showLabels,
  getIsActive,
  wrap = true,
  withMargin = true,
}) {
  const resolvedTools = tools
    .map((tool) => {
      if (typeof tool === "string") return defaultToolMap[tool];
      if (tool && tool.id && !tool.icon) return { ...defaultToolMap[tool.id], ...tool };
      return tool;
    })
    .filter(Boolean);
  const sizeStyle = sizeStyles[size] || sizeStyles.md;
  const resolvedShowLabels = showLabels ?? layout === "stacked";
  const isStacked = layout === "stacked";

  return (
    <div
      className={`${withMargin ? "mb-3" : "mb-0"} flex items-center rounded-lg ${
        wrap ? "flex-wrap" : "flex-nowrap"
      } ${sizeStyle.wrap} ${variantStyles[variant] || variantStyles.glass} ${className}`}
    >
      {resolvedTools.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          type="button"
          title={label}
          aria-label={label}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onAction(id)}
          className={`group flex items-center justify-center rounded-md border border-white/10 bg-white/5 text-white/80 transition hover:border-brand/60 hover:bg-brand/10 hover:text-white ${
            sizeStyle.button
          } ${isStacked ? "flex-col px-2 py-1.5" : ""} ${
            getIsActive && getIsActive(id) ? "border-brand/70 bg-brand/15 text-white" : ""
          }`}
        >
          <Icon className={`${sizeStyle.icon} opacity-90 transition group-hover:opacity-100`} />
          {resolvedShowLabels && (
            <span className={`mt-1 font-medium text-white/70 ${sizeStyle.label}`}>
              {label}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

export default MarkdownToolbar;
